<template>
  <div
    class="base-vertical-table"
    aria-label="Vertikale Tabelle"
  >
    <div
      v-if="!isVertical && !hideHeader"
      :ref="el => { divs[0] = el }"
      :class="'base-vertical-table--row'"
      role="row"
    >
      <div
        v-for="field in currentFields"
        :key="field.key"
        class="base-vertical-table--cell"
        :class="field.class"
        role="columnheader"
      >
        <slot
          :field="field"
          :name="'field:' + field.key"
        >
          <p
            :key="field.style"
            class="base-vertical-table--cell-text"
            :style="field.style"
          >
            {{ field.label }}
          </p>
        </slot>
      </div>
    </div>
    <div
      v-for="(field, index) in isVertical ? currentFields : currentItems"
      :ref="el => { divs[hideHeader ? index : index + 1] = el }"
      :key="field.key"
      :class="{
        'base-vertical-table--row': true,
        'base-vertical-table--row-divider': divider,
        'base-vertical-table--row-last': index === (isVertical ? currentFields : currentItems).length - 1
      }"
      role="row"
    >
      <div
        v-if="isVertical"
        class="base-vertical-table--cell base-vertical-table--cell-header"
        ref="headerCell"
        :class="field.class"
        role="rowheader"
      >
        <slot
          :field="field"
          :name="'field:' + field.key"
        >
          <p
            class="base-vertical-table--cell-text"
            :style="field.style"
          >
            {{ field.label }}
          </p>
        </slot>
      </div>
      <div
        v-for="(item, inx) in isVertical ? currentItems : currentFields"
        :key="`${isVertical ? field.key : item.key}-${inx}`"
        class="base-vertical-table--cell"
        :class="[
          isVertical ? item.class : field.class,
        ]"
        role="cell"
      >
        <slot
          :item="isVertical ? item[field.key] : field[item.key]"
          :name="`item:${isVertical ? field.key : item.key}`"
        >
          <p
            class="base-vertical-table--cell-text"
            :style="item.style"
          >
            {{ isVertical ? item[field.key] : field[item.key] }}
          </p>
        </slot>
      </div>
    </div>
    <div
      v-if="pagination && !!totalItems"
      class="flex items-center justify-between mt-2 base-vertical-table--pagination"
    >
      <div>
        <select
          v-if="itemsPerPageToggle"
          v-model="perPage"
          aria-label="Einträge pro Seite"
          aria-description="Wählt die Anzahl der Einträge pro Seite"
          class="me-4 io-input-sm accessibility-outline"
        >
          <option value="5">
            5 Einträge anzeigen
          </option>
          <option value="10">
            10 Einträge anzeigen
          </option>
          <option value="25">
            25 Einträge anzeigen
          </option>
          <option value="50">
            50 Einträge anzeigen
          </option>
        </select>
        <span class="d-none d-lg-inline">
          {{ currentNumItems }} von {{ totalItems }} Einträgen
        </span>
      </div>
      <BasePagination
        v-if="totalItems > itemsPerPage"
        :current-page="currentPage"
        :length="pageCount"
        hide-goto-first
        hide-goto-last
        @change="change($event)"
      />
    </div>
  </div>
</template>

<script setup>
import {
  defineProps, onMounted, onUnmounted, computed, ref, watch,
} from 'vue';
import IOH from '../../utils/IOH';

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  fields: {
    type: Array,
    default: () => [],
  },
  orientation: {
    type: String,
    default: 'vertical',
  },
  hideHeader: {
    type: Boolean,
    default: false,
  },
  divider: {
    type: Boolean,
    default: false,
  },
  justifyItems: {
    type: String,
    default: 'space-between',
  },
  columnGap: {
    type: String,
    default: '1rem',
  },
  rowGap: {
    type: String,
    default: '1.5rem',
  },
  itemsPerPage: {
    type: Number,
    default: 5,
  },
  itemsPerPageToggle: {
    type: Boolean,
    default: false,
  },
  pagination: {
    type: Boolean,
    default: false,
  },
  list: {
    type: Boolean,
    default: false,
  },
  columnTemplate: {
    type: String,
    default: undefined,
  },
});

const calculatedPadding = computed(() => (
  props.rowGap.replace(/[^0-9.]/g, '') / 2 + props.rowGap.match(/[^0-9.]+/g)[0]
));

const isVertical = computed(() => (
  props.orientation === 'vertical'
));

const rowCount = computed(() => {
  if (props.list) {
    return 1;
  }
  return isVertical.value ? props.items.length + 1 : props.fields.length;
});

const perPage = ref(props.itemsPerPage);
const pageCount = computed(() => (
  Math.ceil(props.items.length / perPage.value)
));
const currentPage = ref(1);
watch(perPage, () => {
  currentPage.value = 1;
});

const internalFields = ref(props.fields);
const currentFields = computed(() => internalFields.value);

const currentNumItems = computed(() => {
  const pageItemCount = perPage.value * currentPage.value;

  return pageItemCount > props.items.length ? props.items.length : pageItemCount;
});
const totalItems = computed(() => (
  props.items.length
));
const internalItems = ref(props.items);
const currentItems = computed(() => {
  const start = (currentPage.value - 1) * perPage.value;
  const end = start + perPage.value;

  return internalItems.value.slice(start, end);
});
const initItems = () => {
  if (!props.items.length) {
    internalFields.value = [{ key: 'msg' }];
    internalItems.value = [{ msg: 'Keine Einträge vorhanden' }];
  } else {
    internalFields.value = props.fields;
    internalItems.value = props.items;
  }
};
initItems();
watch(() => props.items, () => {
  initItems();
});

const change = (page) => {
  currentPage.value = page;
};

const divs = ref([]);
const headerCell = ref(null);
const maxItemWidth = ref('auto');
const maxHeaderWidth = ref('auto');

const getColumnMaxValues = (array) => {
  const columnMaxValues = [];

  for (let i = 0; i < array[0]?.length; i += 1) {
    let max = array[0][i];

    for (let j = 1; j < array.length; j += 1) {
      if (array[j][i] > max) {
        max = array[j][i];
      }
    }

    columnMaxValues.push(max);
  }

  return columnMaxValues;
};

const calculateTable = () => {
  if (divs.value.length) {
    const rowHeights = divs.value.map((field) => {
      const { children } = field;
  
      return [...children].map((child) => child.scrollWidth);
    });
    
    maxItemWidth.value = getColumnMaxValues(rowHeights);
  }

  if (headerCell.value) {
    const headerWidths = headerCell.value.map((field) => {
      const { children } = field;
  
      return [...children].map((child) => child.scrollWidth);
    });
    
    maxHeaderWidth.value = Math.max(...headerWidths);
  }
};

onMounted(() => {
  IOH.animationFrame(() => {
    calculateTable();
  });

  window.addEventListener('resize', () => {
    calculateTable();
  });
});

onUnmounted(() => {
  window.removeEventListener('resize', () => {
    calculateTable();
  });
});

watch(() => maxItemWidth.value, () => {
  const rowHeights = divs.value.map((field) => {
    const { children } = field;

    return [...children].map((child) => child.scrollWidth);
  });

  divs.value.forEach((field) => {
    const { children } = field;

    [...children].forEach((child, inx) => {
      // eslint-disable-next-line no-param-reassign
      child.style.width = `${getColumnMaxValues(rowHeights)[inx]}px`;
    });
  });
});
watch(() => maxHeaderWidth.value, (value) => {
  headerCell.value.forEach((field) => {
    const { children } = field;

    [...children].forEach((child, inx) => {
      // eslint-disable-next-line no-param-reassign
      child.style.width = `${value}px`;
    });
  });
});

const templateColumns = computed(() => {
  if (props.columnTemplate) {
    return props.columnTemplate;
  }
  return `repeat(${rowCount.value}, min-content)`;
});
</script>

<style lang="scss" scoped>
.base-vertical-table {
  display: grid;
  grid-template-columns: 1fr;
  height: min-content;
  overflow-x: auto;

  &--row {
    display: grid;
    grid-template-columns: v-bind(templateColumns);
    justify-content: v-bind(justifyItems);
    gap: v-bind(columnGap);
    padding: v-bind(calculatedPadding) 0;

    &-divider {
      &:not(.base-vertical-table--row-last) {
        border-bottom: 1px solid #e0e0e0;
      }
    }
  }

  &--cell {
    display: flex;
    align-items: center;
  }

  &--cell-text {
    white-space: nowrap;
    line-height: 1;
  }
}
</style>
